// TODO: split these up into utils for client, server or shared

import {
  formatDateLong,
  formatDateMedium,
  formatDateTimeMedium,
} from "@marble/core/modules/timezones/format";
import {
  GUARDIAN_RESOURCES_BUCKET_NAME,
  STUDENT_RESOURCES_BUCKET_NAME,
  THERAPIST_RESOURCES_BUCKET_NAME,
} from "@/config";
import { COMPANY_TZ } from "@/lib/constants";
import { AppointmentTypeEnumToStr } from "@/lib/enumMappings";
import getLogger from "@/lib/logger";
import { addOrSubtractBusinessDays } from "@marble/core/utils/dates";
import type {
  getAppointmentForAdmin,
  getAppointmentForTherapist,
} from "@/lib/modules/appointments/actions";
import { AppointmentWithUserAppointments } from "@/lib/modules/appointments/types";
import {
  Address,
  AppointmentType,
  UserType,
  type Appointment,
  type Group,
  type User,
} from "@prisma/client";
import { clsx, type ClassValue } from "clsx";
import { isValid, parse } from "date-fns";
import { DateTime, type Duration } from "luxon";
import { twMerge } from "tailwind-merge";

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getUserS3ResourcesBucketName = (userType: UserType) => {
  if (userType === "STUDENT") {
    return STUDENT_RESOURCES_BUCKET_NAME;
  } else if (userType === "GUARDIAN") {
    return GUARDIAN_RESOURCES_BUCKET_NAME;
  } else if (userType === "THERAPIST") {
    return THERAPIST_RESOURCES_BUCKET_NAME;
  } else {
    throw new Error(
      `Unsupported client user type ${userType} for S3 resources bucket`,
    );
  }
};

/**
 * Check if two lists are equal, regardless of order.
 *
 * @param list1
 * @param list2
 * @returns
 */
export const areListsEqual = <T>(list1: T[], list2: T[]): boolean => {
  if (list1.length !== list2.length) {
    return false;
  }

  const sortedList1 = [...list1].sort();
  const sortedList2 = [...list2].sort();

  return sortedList1.every((item, index) => item === sortedList2[index]);
};

export const joinStringsWithAnd = (strings: string[]): string => {
  if (strings.length === 0) return "";
  if (strings.length === 1) return strings[0];
  if (strings.length === 2) return `${strings[0]} and ${strings[1]}`;

  const last = strings.pop();
  return `${strings.join(", ")}, and ${last}`;
};

/**
 * Only can be used on client side.
 * Detects if there's a mobile browser being used.
 *
 * @returns boolean for whether or not the user is on a mobile device.
 */
export const isMobile = () => {
  if (typeof window === "undefined") {
    return false;
  }

  // In this case, use user agent as a fallback to avoid false positives.
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUA =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent,
    );
  // Only return true if the user agent clearly indicates mobile
  return isMobileUA;
};

/**
 * Because you cant raise errors server side that are caught
 * clientside, we use this class to wrap errors that are thrown
 * server side and return the message to the client side.
 *
 * Example usage:
 *
 * Server side:
 *   const doSomethingAction = async () => { *
 *     try {
 *       await doSomething();
 *     } catch (error) {
 *      logger.error(error, "Error caught doing something");
 *      return ServerActionError(error);
 *     }
 *   }
 *
 * Client side:
 *  const result = await doSomethingAction();
 *
 *   if (isServerActionError(result)) {
 *     // handle error
 *   } else {
 *     // handle success
 *     console.log(result);
 *   }
 */
export const ServerActionError = (error: unknown): TServerActionError => ({
  error:
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "Unknown error",
});

export const isServerActionError = (
  result: any,
): result is TServerActionError => {
  return result?.error !== undefined;
};

type TServerActionError = {
  error: string;
};

export type ServerActionResult<T> = Promise<T | TServerActionError>;

/**
 * Detects whether an error represents an aborted operation — most commonly a
 * client disconnecting mid-request, which fires the `AbortSignal` we thread
 * into an in-flight `fetch` / tRPC call. tRPC surfaces this as a
 * `TRPCClientError` whose message is "This operation was aborted" and whose
 * `cause` is a DOMException named "AbortError", so we walk the cause chain and
 * check both the name and the message. This lets callers treat a caller-side
 * cancellation as the benign, expected outcome it is rather than a real server
 * error. Timeouts (DOMException "TimeoutError") are intentionally NOT treated
 * as aborts.
 */
export const isAbortError = (error: unknown): boolean => {
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current instanceof Error && !seen.has(current)) {
    seen.add(current);
    if (current.name === "AbortError") {
      return true;
    }
    if (current.message.toLowerCase().includes("operation was aborted")) {
      return true;
    }
    current = (current as { cause?: unknown }).cause;
  }
  return false;
};

/**
 * Sleep for given number of milliseconds.
 *
 * @param ms
 * @returns
 */
export const getAppUrl = (path: string, overrideDomain?: string) => {
  // Normalize path to not have leading slash
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

  // Get base URL and ensure it has trailing slash
  const baseUrl = overrideDomain || process.env.NEXT_PUBLIC_APP_URL || "";
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

  return `${normalizedBaseUrl}${normalizedPath}`;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const capitalCase = (str: string): string => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const arrayToCSVString = (array: any[][]) =>
  array
    .map(
      (row) =>
        row
          .map(String) // convert every value to String
          .map((v) => v.replaceAll('"', '""')) // escape double quotes
          .map((v) => `"${v}"`) // quote it
          .join(","), // comma-separated
    )
    .join("\r\n"); // rows starting on new lines

export const downloadFile = (
  filename: string,
  content: string,
  contentType: string,
) => {
  // Create a blob
  var blob = new Blob([content], { type: contentType });
  var url = URL.createObjectURL(blob);

  // Create a link to download it
  var pom = document.createElement("a");
  pom.href = url;
  pom.setAttribute("download", filename);
  pom.click();
};

/**
 * Given a DOB string in mm/dd/yyyy format, make sure
 * the month and day are two digits and the year is 4 digits.
 *
 * @param dateString
 */
export const formatDateOfBirthString = (
  dateString: string | undefined,
): string => {
  if (!dateString) {
    return "";
  }
  let [month, day, year] = dateString.split("/");

  // Avoid throwing on partial or malformed dates (e.g. "1/2").
  // Keep the original value so upstream validation can surface errors.
  if (!month || !day || !year) {
    return dateString;
  }

  if (month.length === 1) {
    month = `0${month}`;
  }
  if (day.length === 1) {
    day = `0${day}`;
  }
  if (year.length === 2) {
    year = `20${year}`;
  }

  return `${month}/${day}/${year}`;
};

/**
 * Use to get the return type of an async function.
 *
 * example:
 *   const asyncFunction = async (): Promise<T> => {...}
 *   type asyncFunctionReturnType = AsyncReturnType<typeof asyncFunction>
 */
export type AsyncReturnType<T extends (...args: any) => Promise<any>> =
  T extends (...args: any) => Promise<infer R> ? R : any;

/**
 * Parses a date string into a Date object.
 *
 * @param dateString
 * @returns Date
 */
export const parseDate = (dateString: string): Date => {
  const dateObj = new Date(dateString);
  var userTimezoneOffset = dateObj.getTimezoneOffset() * 60000;
  return new Date(dateObj.getTime() + userTimezoneOffset);
};

/**
 * "June 15, 1998" from a stored date-of-birth string.
 *
 * A birthday is a calendar date, not an instant, and it is stored as one:
 * every `dateOfBirthString` in the database is `MM/DD/YYYY`. So the day is
 * read straight off the string and rebuilt at UTC noon, never parsed from
 * the text by `Date`.
 *
 * That detail is the whole point. `new Date("06/15/1998")` — the slash form
 * we actually store — parses to *local* midnight, not UTC midnight, so
 * rendering it in UTC shifts the day back for any reader east of Greenwich.
 * Building the instant ourselves means no zone, anywhere, can move it.
 *
 * Returns the input unchanged when it isn't a date we can read, which a
 * handful of rows are.
 */
export const formatBirthdate = (dobString: string): string => {
  const parts = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dobString.trim());
  if (!parts) {
    return dobString;
  }
  const [, month, day, year] = parts;
  return formatDateLong(
    new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12)),
    { timezone: "UTC" },
  );
};

/**
 * Converts a date string from DynamoDB/database format (usually ISO format)
 * to the MM/dd/yyyy format expected by DatePickerInput component.
 *
 * @param dateString - Date string in any format that Date constructor can parse
 * @returns Date string in MM/dd/yyyy format, or empty string if invalid
 */
export const formatDateForInput = (
  dateString?: string,
  format: string = "MM/dd/yyyy",
): string => {
  if (!dateString) return "";
  try {
    const date = DateTime.fromJSDate(new Date(dateString));
    if (!date.isValid) return "";
    return date.toFormat(format);
  } catch (error) {
    return "";
  }
};

/** The shape `DatePickerInput` reads and writes in its text box. */
const CALENDAR_DATE_INPUT_FORMAT = "MM/dd/yyyy";

/**
 * The same job as `formatDateForInput`, for a stored **calendar day** rather
 * than an instant.
 *
 * The two cannot share one function. `formatDateForInput` parses with
 * `new Date()` and renders on the local clock, which is right for the call
 * log — it is handed a full ISO instant and a 9:30pm Eastern call belongs on
 * the 29th, not the 30th. It is wrong for a date-only string: `new Date()`
 * reads `"2028-09-01"` as UTC midnight, so a US browser renders 08/31/2028
 * and the form offers to save a day earlier than what is stored.
 *
 * Credentialing dates are date-only and are stored in two shapes, because
 * two forms write them: `MM/DD/YYYY` from the credentialing forms and
 * `YYYY-MM-DD` from the licence-data task. This parses either and never
 * shifts the day.
 */
export const formatCalendarDateForInput = (dateString?: string): string => {
  if (!dateString) return "";
  for (const shape of [CALENDAR_DATE_INPUT_FORMAT, "yyyy-MM-dd"]) {
    const parsed = DateTime.fromFormat(dateString.trim(), shape);
    if (parsed.isValid) {
      return parsed.toFormat(CALENDAR_DATE_INPUT_FORMAT);
    }
  }
  return "";
};

export const getNextBusinessDayAtTime = (
  hour: number,
  minute: number,
  timezone: string = COMPANY_TZ,
): Date => {
  let nextDay = DateTime.now()
    .setZone(timezone)
    .plus({ days: 1 })
    .set({ hour, minute, second: 0, millisecond: 0 });

  // Skip weekends
  while (nextDay.weekday === 6 || nextDay.weekday === 7) {
    nextDay = nextDay.plus({ days: 1 });
  }

  return nextDay.toJSDate();
};

/* Returns the end of business day Datetime for dt. Adds 2 days if dt is a Friday to account for the weekend. */
const pushDateToEndOfBusinessDay = (dt: DateTime) => {
  return dt.weekday === 5 ? dt.plus({ days: 2 }).endOf("day") : dt.endOf("day");
};

/**
 * Returns the cutoff Date for an "upcoming within N business days" filter.
 * N=0 → end of today; N≥1 → end of the Nth business day starting from tomorrow.
 */
export const getUpcomingWithinBusinessDaysCutoff = (
  businessDays: number,
): Date => {
  // consider appointments happening on the weekend as happening on the "Fridayth" business day. Eg. if Friday is 3rd business day,
  // Saturday appointments are also considered as happening on the 3rd business day.

  if (businessDays === 0) {
    const now = DateTime.now().setZone(COMPANY_TZ).endOf("day");
    return pushDateToEndOfBusinessDay(now).toJSDate();
  }
  const anchorStart = getNextBusinessDayAtTime(0, 0);
  const dayStart = addOrSubtractBusinessDays(anchorStart, businessDays - 1);
  return pushDateToEndOfBusinessDay(
    DateTime.fromJSDate(dayStart, { zone: COMPANY_TZ }),
  ).toJSDate();
};

/**
 * Returns { start, end } for the Nth business day (for an "on the Nth business day" filter).
 * N=0 → today; N≥1 → Nth business day starting from tomorrow (same day as Within:N cutoff).
 */
export const getUpcomingOnBusinessDayCutoff = (
  businessDays: number,
): { start: Date; end: Date } => {
  // consider fridays as including the weekend. Eg. if the filter is "on the 2nd business day" and friday is the 2nd business day out,
  // then Saturday and Sunday appointments will also be included in the filter.

  if (businessDays === 0) {
    const now = DateTime.now().setZone(COMPANY_TZ).startOf("day");
    return {
      start: now.toJSDate(),
      end: pushDateToEndOfBusinessDay(now).toJSDate(),
    };
  }
  const anchorStart = getNextBusinessDayAtTime(0, 0);
  const dayStart = addOrSubtractBusinessDays(anchorStart, businessDays - 1);
  const dt = DateTime.fromJSDate(dayStart, { zone: COMPANY_TZ }).startOf("day");
  return {
    start: dt.toJSDate(),
    end: pushDateToEndOfBusinessDay(dt).toJSDate(),
  };
};

/**
 * Formats a date string (e.g. "2025-11-03" or "2025/11/03") to YYYY-MM-DD
 *
 * NOTE: Assumes we want to keep the date in the string, so e.g.
 * "2025-11-03T00:00:00.000Z" becomes "2025-11-03" (even though it's actually 2025-11-02 in EST)
 */
export const formatDateStringISO = (date: string): string => {
  return DateTime.fromJSDate(new Date(date)).toUTC().toFormat("yyyy-MM-dd");
};

/**
 * "Jun 1, 2026" from an ISO string. Returns "" for empty or unparseable
 * input. The caller supplies the zone, because which calendar day an
 * instant falls on depends on it.
 */
export const formatIsoDate = (
  iso: string | undefined,
  timezone: string,
): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : formatDateMedium(d, { timezone });
};

/**
 * "Jun 1, 2026 at 5:00 PM EDT" from an ISO string. Returns "" for empty or
 * unparseable input.
 */
export const formatIsoDateTime = (
  iso: string | undefined,
  timezone: string,
): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : formatDateTimeMedium(d, { timezone });
};

export const formatUTCDate = (date?: Date): string => {
  if (!date) return "";
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
};

/** Whole minutes between two instants (rounded to the nearest minute). */
export const getDurationMinutes = (start: Date, end: Date): number =>
  Math.round((end.getTime() - start.getTime()) / 60_000);

export const formatApptDuration = (start: Date, end: Date): string => {
  return `${getDurationMinutes(start, end)} minutes`;
};

export const formatDuration = (duration: Duration): string => {
  return duration.rescale().toHuman({ showZeros: false });
};

/** The number of days between two dates (defaults to today as the end date). */
export const daysSince = (date: Date | DateTime, endDate?: Date | DateTime) => {
  const startDT = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  endDate = endDate instanceof Date ? DateTime.fromJSDate(endDate) : endDate;
  const endDT = endDate ?? DateTime.now();
  return Math.floor(endDT.diff(startDT, "days").days);
};

/**
 * Calculate age from dateOfBirthString in MM/dd/yyyy format.
 * Returns 0 if dateOfBirthString is null, undefined, or invalid.
 *
 * @param dobString - Date string in MM/dd/yyyy format
 * @returns Age in years, or 0 if invalid
 */
export const age = (dobString: string | null | undefined): number => {
  if (!dobString) {
    return 0;
  }

  try {
    // Try parsing as MM/dd/yyyy format first (therapist format)
    let dob = parse(dobString, "MM/dd/yyyy", new Date());

    // If that fails, try standard Date parsing (for ISO dates, etc.)
    if (!isValid(dob)) {
      dob = new Date(dobString);
      if (isNaN(dob.getTime())) {
        return 0;
      }
    }

    const diff = Date.now() - dob.getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  } catch {
    return 0;
  }
};

/**
 * Returns true when a student is an adult (no guardian) aged 21 or older.
 * Missing DOB defaults to true — safe/private default since most adults in
 * the system are parents well over 21.
 */
export const getIsAdultOver21 = (
  isAdult: boolean,
  dateOfBirthString: string | null | undefined,
): boolean => isAdult && (!dateOfBirthString || age(dateOfBirthString) >= 21);

/**
 * Returns the client's age for scheduling/matching purposes.
 * Returns null when DOB is unknown — callers receiving null must explicitly
 * decide to skip age-based filtering. DOB is backfilled from insurance when
 * available (see upsertInsuranceInfo).
 */
export const getClientAgeForScheduling = (
  dobString: string | null | undefined,
): number | null => {
  const computed = age(dobString);

  //age() returns 0 when the DOB is null, undefined, or invalid
  if (computed === 0) {
    return null;
  }

  return computed;
};

// https://stackoverflow.com/questions/8358084/regular-expression-to-reformat-a-us-phone-number-in-javascript
export function formatPhoneNumber(
  phoneNumber: string | null | undefined,
  hideIntl = true,
) {
  if (!phoneNumber) return null;
  var cleaned = ("" + phoneNumber).replace(/\D/g, "");
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = match[1] && !hideIntl ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return null;
}

export const getGuardianConsultParticipantName = (
  userAppointments: {
    isBilledClient: boolean;
    isHost: boolean;
    User: { firstName: string | null; lastName: string | null };
  }[],
): string | undefined => {
  const guardian = userAppointments.find(
    (ua) => !ua.isBilledClient && !ua.isHost,
  );
  if (!guardian) return undefined;
  return `${guardian.User.firstName} ${guardian.User.lastName}`.trim();
};

export const formatAppointmentTitle = (
  appointmentType: AppointmentType,
  client: { User: { firstName: string | null; lastName: string | null } },
  opts?: { guardianName?: string },
): string => {
  if (
    appointmentType === AppointmentType.GUARDIAN_CONSULT &&
    opts?.guardianName
  ) {
    return `${AppointmentTypeEnumToStr[appointmentType]} for ${client.User.firstName} with ${opts.guardianName}`;
  }
  const prep =
    appointmentType === AppointmentType.GUARDIAN_CONSULT ? "for" : "with";
  return `${AppointmentTypeEnumToStr[appointmentType]} ${prep} ${client.User.firstName} ${client.User.lastName}`;
};

export const formatGroupAppointmentTitle = (
  appointmentType: AppointmentType,
  students: any[],
): string => {
  return `${AppointmentTypeEnumToStr[appointmentType]} with ${students.length} clients`;
};

type FormatSupervisorTitleParams = {
  appointment: Appointment;
  group?: Group;
  clientName?: string;
  superviseeTherapistUser: User;
};

export const formatAppointmentTitleForSupervisor = ({
  appointment,
  group,
  clientName,
  superviseeTherapistUser,
}: FormatSupervisorTitleParams) => {
  if (appointment.appointmentType === "GROUP_THERAPY" && group) {
    return `${superviseeTherapistUser.firstName} ${superviseeTherapistUser.lastName}'s ${group.name} group appointment`;
  } else {
    return `${superviseeTherapistUser.firstName} ${superviseeTherapistUser.lastName}'s appointment with ${clientName}`;
  }
};

export type $TSFIXME = unknown;

export const centsFromDollars = (amount: number) => Math.round(amount * 100);
export const dollarsFromCents = (cents?: number): number => {
  if (!cents) return 0;
  return cents / 100;
};
export const formatCentsToDollars = (cents?: number): string => {
  if (!cents) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
};

export const formatStatusDisplay = (status?: string): string => {
  if (!status) return "";
  return status.charAt(0) + status.slice(1).toLowerCase();
};

// Headway formats as 2 digits before decimal, e.g. F411 -> F41.1
export const formatDiagnosisCodeForHeadway = (code: string): string =>
  code.substring(0, 3) + "." + code.substring(3);

/**
 * Custom JSON.stringify to handle BigInts.
 *
 * @param str
 * @returns
 */
export const toTitleCase = (str: string): string => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/** Capitalize the first letter of each sentence in the given text. */
export const capitalizeSentences = (text: string) => {
  if (!text || typeof text !== "string") return "";
  return text.replace(/(^\s*\w|[\.\!\?]\s*\w)/g, (match) =>
    match.toUpperCase(),
  );
};

export const getDaysAgo = (days: number, date: Date = new Date()) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - days);
  return newDate;
};

export const getOneDayDateRange = (
  daysAgo: number,
  weekdays: boolean = true,
) => {
  const now = new Date(); // Current date and time
  let startRange = new Date(now); // Clone to avoid mutations
  let endRange = new Date(now);

  let daysCounted = 0; // Track the number of weekdays counted

  while (daysCounted < daysAgo) {
    now.setUTCDate(now.getUTCDate() - 1);

    // If weekdays is true, skip weekends
    if (!weekdays || (now.getUTCDay() >= 1 && now.getUTCDay() <= 5)) {
      daysCounted++;
    }
  }

  startRange = new Date(now); // Set the start range based on the counted date
  startRange.setUTCHours(0, 0, 0, 0); // Set to start of day

  // Calculate end range: move one day forward from the start range
  endRange = new Date(startRange);
  endRange.setUTCDate(startRange.getUTCDate() + 1);

  return [startRange, endRange];
};

/**
 * Get a date range given a start date and a number of days to add or subtract.

 *
 * Examples:
 *   - getDateRange(new Date("2023-10-01"), -5) // returns [2023-09-26 (12am), 2023-10-01 (11:59:59pm)]
 *   - getDateRange(new Date("2023-10-01"), 5) // returns [2023-10-01 (12am), 2023-10-06 (11:59:59pm)]
 *
 * @param date The reference date for the range.
 * @param daysDiff Number of days to subtract or add to the range.
 * @returns A tuple with the start (and end dates of the range.
 */
export const getDateRange = (date: Date, daysDiff: number): [Date, Date] => {
  const start = new Date(date.setHours(0, 0, 0, 0));
  const end = new Date(start.setHours(23, 59, 59, 999));

  if (daysDiff < 0) {
    start.setDate(start.getDate() + daysDiff);
  } else {
    end.setDate(end.getDate() + daysDiff);
  }

  return [start, end];
};

export const getClientUserAppointmentFromAppointment = (
  appointment: AsyncReturnType<
    typeof getAppointmentForTherapist | typeof getAppointmentForAdmin
  >,
) => {
  if (!appointment) return;

  return appointment.UserAppointments.find((ua) => ua.isBilledClient);
};

export const concatUrlPaths = (...paths: string[]): string => {
  return paths
    .map((path) => path.replace(/^\/|\/$/g, ""))
    .join("/")
    .replace(/\/+/g, "/");
};

export const formatBlockNoteText = (text: string) => {
  // Format the note text to handle line breaks for block quotes
  return text
    .split("\n") // Split the note into lines
    .map((line) => `> ${line}`) // Prefix each line with '>'
    .join("\n"); // Join the lines back with newline characters
};

export const getIsAutoresponderHours = () => {
  const nowET = DateTime.now().setZone(COMPANY_TZ);

  // Autoresponder hours: Friday 6pm - Sunday 11:59pm
  const isWeekend = nowET.weekday === 6 || nowET.weekday === 7; // Saturday = 6, Sunday = 7
  const isEndOfDayFriday = nowET.weekday === 5 && nowET.hour >= 18; // Friday = 5

  return isWeekend || isEndOfDayFriday;
};

export const formatAddress = (address: Address) => {
  return `${address.line1}${address.line2 ? ` ${address.line2}` : ""}, ${address.city}, ${address.state} ${address.zipCode}`;
};

// Moved to `@marble/core` with the appointment comms channel write.
export { isValidEmail } from "@marble/core/utils/email";

export const checkForQQEmail = (email: string | undefined | null): boolean => {
  if (!email) return false;
  return email.toLowerCase().trim().endsWith("@qq.com");
};

/**
 * Detects if an email is a fake/test email pattern
 * @param email - The email to check
 * @returns true if the email appears to be fake
 */
export const isFakeEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;

  const lowerEmail = email.toLowerCase();

  // Common fake email patterns
  const fakePatterns = [
    // Generic fake patterns
    /^na@(msn|gmail|yahoo|hotmail|outlook)\.com$/,
    /^test@/,
    /^fake@/,
    /^dummy@/,
    /^example@/,
    /^sample@/,
    /^temp@/,
    /^temporary@/,
    /^placeholder@/,
    /^no@/,
    /^none@/,
    /^n\/a@/,
    /^na@/,
    /^null@/,
    /^unknown@/,
    /^undefined@/,
    // single character patterns
    /^[a-z]@/,
    /^[0-9]@/,
    // Numeric-only patterns (e.g., 000@00.com, 123@456.com)
    /^[0-9]+@[0-9]+\.[a-z]{2,}$/,
    /^[0]+@[0]+\.[a-z]{2,}$/, // 0@0.com, 00@00.com
    /^[1-9]@[1-9]\.[a-z]{2,}$/, // 1@1.com, 9@9.com
    // known fake domains
    /@na\.com$/,
    /@test\.com$/,
  ];

  return fakePatterns.some((pattern) => pattern.test(lowerEmail));
};

export const downloadTextContent = async (url: string) => {
  const maxRetries = 3;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (
        contentType &&
        !contentType.includes("text/") &&
        !contentType.includes("application/json")
      ) {
        throw new Error(`Expected text content but got: ${contentType}`);
      }

      const textContent = await response.text();

      if (textContent.length === 0) {
        throw new Error("Empty text content received");
      }

      if (textContent.includes("<Error>") || textContent.includes("<?xml")) {
        throw new Error("Received error response instead of text content");
      }

      return textContent;
    } catch (error) {
      if (attempt < maxRetries - 1) {
        getLogger("downloadTextContent").warn(
          error,
          `Error downloading text content from URL (attempt ${attempt + 1}/${maxRetries}), retrying`,
        );
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      } else {
        getLogger("downloadTextContent").error(
          error,
          "Error downloading text content from URL",
        );
        return undefined;
      }
    }
  }
};

export const parseErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
};

/**
 * Gets the number of days between two dates
 */
export const getDaysBetween = (start: Date, end: Date): number => {
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Whether an appointment is far enough in the future that it can't be
 * submitted yet — more than a full day out. Shared by the appointment form
 * (which disables submit) and the V2 appointment page (which surfaces the
 * blocker up top) so the two can't drift.
 */
export const isAppointmentInFuture = (startTime: Date): boolean => {
  const now = new Date();
  return startTime > now && getDaysBetween(now, startTime) > 0;
};

export const wasClientNoShow = (
  appointment: AppointmentWithUserAppointments,
) => {
  return appointment.UserAppointments.some((ua) => !ua.isHost && ua.noShowed);
};

/**
 * Format a message timestamp in a human-readable way
 * - "Just now" for messages less than 1 minute old
 * - "Today at 3:45 PM" for messages from today
 * - "Monday at 3:45 PM" for messages from the last week
 * - "12/25 3:45 PM" for older messages
 */
export function formatMessageTime(messageTime: Date, timezone: string): string {
  const now = new Date();
  const msPerMinute = 60 * 1000;
  const msPerWeek = msPerMinute * 60 * 24 * 7;
  const timeDifference = now.getTime() - messageTime.getTime();

  // "Just now" if the message was sent in the last minute
  if (timeDifference < msPerMinute) {
    return "Just now";
  }

  const zoned = DateTime.fromJSDate(messageTime, { zone: timezone });
  const time = zoned.toFormat("h:mm a ZZZZ");

  // Which calendar day a message landed on is itself zone-dependent, so
  // "Today" has to be decided on the same clock the time is rendered in —
  // reading it off the browser put a late-evening Eastern message on
  // tomorrow for an admin further east.
  const today = DateTime.now().setZone(timezone);
  if (zoned.hasSame(today, "day")) {
    return `Today at ${time}`;
  }

  if (timeDifference < msPerWeek) {
    return `${zoned.toFormat("cccc")} at ${time}`;
  }

  return `${zoned.toFormat("M/d")} ${time}`;
}

/*
 * Converts a Next.js searchParams object to URLSearchParams.
 * Handles array values and filters out undefined values.
 *
 * @param searchParams - Next.js searchParams object
 * @returns URLSearchParams instance
 *
 * @example
 * searchParamsToURLSearchParams({ foo: 'bar', baz: ['a', 'b'] }).toString()
 * // Returns: "foo=bar&baz=a&baz=b"
 */
export const searchParamsToURLSearchParams = (searchParams: {
  [key: string]: string | string[] | undefined;
}): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(searchParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => v && params.append(key, v));
    } else if (value) {
      params.append(key, value);
    }
  });
  return params;
};
/**
 * Utility to get the time delta in MS for the difference between two dates
 */

/**
 * Lazy-loads and calls revalidatePath only when in a Next.js environment.
 * Silently skips revalidation in Lambda or other non-Next.js runtimes.
 *
 * This prevents crashes when shared server action modules are imported by
 * Lambda code, as `next/cache` depends on `server-only` which throws errors
 * outside of Next.js.
 *
 * @param path - The path to revalidate (e.g., "/appointments/123")
 */
export const revalidatePathIfAvailable = async (path: string) => {
  // Skip revalidation in Lambda/non-Next.js environments
  if (typeof window === "undefined" && !process.env.NEXT_RUNTIME) {
    return;
  }
  try {
    const { revalidatePath } = await import("next/cache");
    await revalidatePath(path);
  } catch {
    // Silently fail if next/cache is not available (e.g., in Lambda)
  }
};

// Business-day math moved to `@marble/core/utils/dates` so the crons that
// create task SLAs can share it. Re-exported here because most of the app
// reaches for these through `@/lib/utils`.
export {
  addOrSubtractBusinessDays,
  countBusinessDaysDiff,
  pushDateToLastBusinessDay,
} from "@marble/core/utils/dates";
// Moved to `@marble/core` with the Zendesk client, which uses both.
export { sleep } from "@marble/core/utils/async";
export { jsonStringify } from "@marble/core/utils/json";
